import { Link } from "react-router-dom";
import { 
  FlaskConical, 
  RefreshCw, 
  Cog, 
  Package, 
  Palette, 
  Beaker,
  Factory,
  Award,
  FileText,
  ArrowRight
} from "lucide-react";
import Footer from "@/components/Footer";

const services = [
  {
    icon: FlaskConical,
    title: "Desarrollo de Fórmula",
    description: "Creamos fórmulas únicas desde cero, adaptadas a tus especificaciones de sabor, funcionalidad y perfil nutricional.",
    features: ["Perfil de sabor personalizado", "Ingredientes funcionales", "Estabilidad garantizada"],
  },
  {
    icon: RefreshCw,
    title: "Reformulación",
    description: "Optimizamos fórmulas existentes para mejorar sabor, reducir costes o adaptar a nuevas regulaciones.",
    features: ["Análisis de fórmula actual", "Propuestas de mejora", "Pruebas comparativas"],
  },
  {
    icon: Cog,
    title: "Optimización Industrial",
    description: "Adaptamos tu fórmula para producción a gran escala sin comprometer calidad ni características.",
    features: ["Escalado de producción", "Eficiencia de costes", "Consistencia de lote"],
  },
  {
    icon: Package,
    title: "Diseño de Envase",
    description: "Asesoramiento completo en selección de envase, cierre y materiales según tu producto y mercado.",
    features: ["Catálogo de opciones", "Compatibilidad de producto", "Soluciones sostenibles"],
  },
  {
    icon: Palette,
    title: "Desarrollo de Marca",
    description: "Apoyo en naming, identidad visual y diseño de etiquetado para tu nueva bebida.",
    features: ["Generación de nombres con IA", "Diseño de etiqueta", "Mockups de producto"],
  },
  {
    icon: Beaker,
    title: "Producción Piloto",
    description: "Lotes de prueba para validar fórmula, envase y procesos antes de la producción final.",
    features: ["Lotes de 100-1000 unidades", "Validación de fórmula", "Pruebas de mercado"],
  },
  {
    icon: Factory,
    title: "Producción Industrial",
    description: "Producción a escala con nuestras líneas automatizadas de embotellado y envasado.",
    features: ["Alta capacidad productiva", "Múltiples formatos", "Flexibilidad de volumen"],
  },
  {
    icon: Award,
    title: "Control de Calidad",
    description: "Análisis completo de cada lote producido en nuestro laboratorio certificado.",
    features: ["Análisis microbiológico", "Control organoléptico", "Certificaciones"],
  },
  {
    icon: FileText,
    title: "Gestión Regulatoria",
    description: "Asesoramiento en normativa, etiquetado y requisitos legales para tu mercado objetivo.",
    features: ["Normativa EU/nacional", "Etiquetado legal", "Registro de producto"],
  },
];

export default function ServicesPage() {
  return (
    <main className="min-h-screen" data-testid="services-page">
      {/* Hero */}
      <section className="relative py-32 pt-40 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-4 block">
              Servicios
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-udl-white mb-6">
              Todo lo que necesitas para <span className="text-udl-primary">crear tu bebida</span>
            </h1>
            <p className="text-udl-gray-400 text-lg leading-relaxed">
              Desde la idea inicial hasta el producto final en estantería. Ofrecemos todos los 
              servicios necesarios para desarrollar, producir y lanzar tu bebida al mercado.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-udl-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <div key={i} className="card-feature group">
                <service.icon className="text-udl-primary mb-4 group-hover:scale-110 transition-transform duration-300" size={32} />
                <h3 className="text-udl-white font-heading font-bold text-xl mb-3">{service.title}</h3>
                <p className="text-udl-gray-400 text-sm leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-udl-gray-400 text-xs">
                      <div className="w-1.5 h-1.5 bg-udl-primary" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 bg-udl-surface relative">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-udl-white mb-6">
            ¿Listo para empezar?
          </h2>
          <p className="text-udl-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Configura tu bebida en nuestro sistema y recibe un dossier técnico profesional 
            para iniciar el desarrollo.
          </p>
          <Link to="/configurador" className="btn-primary inline-flex items-center gap-2" data-testid="services-cta">
            Ir al configurador
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
